import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LanguageService } from 'src/app/multi-language/language.service';
import { FeatherModule } from 'angular-feather';
import { Linkedin } from 'angular-feather/icons';
import { CpCompanyService } from 'src/app/admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';
import { Router, RouterModule } from '@angular/router';
import { SocialMediaService, SocialLink } from 'src/app/Theme/shared/service/social-media.service';
import { FooterStore } from 'src/app/Theme/shared/service/footer-store.service';
import { Subscription } from 'rxjs';
import { AdAdminService } from 'src/app/admin/ad-admin/ad-admin.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, TranslateModule, FeatherModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit, OnDestroy {
  logoImageUrl: string;
  /** Link for the main logo (saved in admin footer support logos, stored in localStorage) */
  logoLinkUrl: string = '';
  tagLine: string = '';
  socialLinks: SocialLink[] = [];
  groupedLinks: Array<{ key: string; name: string; icon: string; links: SocialLink[] }> = [];
  openGroupKey: string | null = null;
  supportLogos: Array<{ url: string; link: string }> = [];
  companyName: string;
  copyrightText: string = '';
  currentYear: number = new Date().getFullYear();
  readonly zumConnectPortalUrl = 'https://p2-innovate.com/offering/zum-connect-customer-portal/';

  private readonly iconMap: Record<string, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'Linkedin',
    twitter: 'Twitter'
  };

  private subscriptions: Subscription[] = [];

  constructor(
    public translate: TranslateService,
    public languageService: LanguageService,
    public CpCompanyService: CpCompanyService,
    private router: Router,
    private socialMediaService: SocialMediaService,
    private adAdminService: AdAdminService,
    private footerStore: FooterStore
  ) { }

  private static readonly FOOTER_MAIN_LOGO_LINK_KEY = 'footer_main_logo_link';

  ngOnInit(): void {
    this.logoLinkUrl = localStorage.getItem(FooterComponent.FOOTER_MAIN_LOGO_LINK_KEY) || '';

    // Load fresh support logos from API every time footer is shown
    this.adAdminService.getSupportLogos().subscribe({
      next: (response) => {
        const supportLogos = this.extractSupportLogos(response);
        this.footerStore.updateData({ supportLogos });
      },
      error: (error) => {
        console.error('Error loading support logos:', error);
      }
    });

    // Subscribe to the logo URL from the service (already loaded during app initialization)
    const logoSub = this.CpCompanyService.pictureUrlLogo$.subscribe(url => {
      if (url) {
        this.logoImageUrl = url;
      }
    });
    this.subscriptions.push(logoSub);

    // Get initial logo URL if already loaded
    const currentLogoUrl = this.CpCompanyService.getCurrentLogoUrl();
    if (currentLogoUrl) {
      this.logoImageUrl = currentLogoUrl;
    }

    this.companyName = 'ZUM IT';
    this.updateCopyrightText();

    // Subscribe to footer store for tagline and support logos
    const footerSub = this.footerStore.state$.subscribe(state => {
      if (state.data.tagLine) {
        this.tagLine = state.data.tagLine;
      }
      if (state.data.supportLogos && state.data.supportLogos.length > 0) {
        this.supportLogos = state.data.supportLogos;
      }
      // Fallback to logo from store if not in CpCompanyService
      if (state.data.logoUrl && !this.logoImageUrl) {
        this.logoImageUrl = state.data.logoUrl;
      }
    });
    this.subscriptions.push(footerSub);

    // Get initial footer data from store (already loaded during app initialization)
    const footerData = this.footerStore.getCurrentData();
    if (footerData.tagLine) {
      this.tagLine = footerData.tagLine;
    }
    if (footerData.supportLogos && footerData.supportLogos.length > 0) {
      this.supportLogos = footerData.supportLogos;
    }
    if (footerData.logoUrl && !this.logoImageUrl) {
      this.logoImageUrl = footerData.logoUrl;
    }

    // Social links (already loaded during app initialization)
    const socialSub = this.socialMediaService.links$.subscribe(links => {
      this.socialLinks = links;
      this.groupedLinks = this.groupByPlatform(links);
    });
    this.subscriptions.push(socialSub);

    // Get initial social links if already loaded
    const currentLinks = this.socialMediaService.getCurrentLinks();
    if (currentLinks && currentLinks.length > 0) {
      this.socialLinks = currentLinks;
      this.groupedLinks = this.groupByPlatform(currentLinks);
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Extract support logos from API response
   */
  private extractSupportLogos(data: any): Array<{ url: string; link: string }> {
    if (!data) { return []; }
    const arr = Array.isArray(data) ? data : (data?.results || data?.data || []);
    return (arr || []).map((it: any) => ({
      url: it.logo || it.logo_url || '',
      link: it.logo_link || '#'
    })).filter(x => !!x.url);
  }

  navigateToAboutUs(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    console.log('Opening About Us in new tab...');
    window.open('/about', '_blank');
  }

  navigateToPrivacyPolicy(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    console.log('Opening Privacy Policy in new tab...');
    window.open('/privacy', '_blank');
  }

  navigateToTermsOfUse(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    console.log('Opening Terms Of Use in new tab...');
    window.open('/terms', '_blank');
  }

  getIconName(name: string): string {
    if (!name) { return 'Link'; }
    const key = name.toString().trim().toLowerCase();
    return this.iconMap[key] || 'Link';
  }

  private groupByPlatform(links: SocialLink[]): Array<{ key: string; name: string; icon: string; links: SocialLink[] }> {
    const map = new Map<string, SocialLink[]>();
    for (const l of links || []) {
      const key = (l.name || '').toString().trim().toLowerCase();
      if (!key) { continue; }
      if (!map.has(key)) { map.set(key, []); }
      map.get(key)!.push(l);
    }
    return Array.from(map.entries()).map(([key, arr]) => ({
      key,
      name: arr[0]?.name || key,
      icon: this.getIconName(key),
      links: arr
    }));
  }

  getLinkLabel(url: string): string {
    if (!url) { return ''; }
    try {
      const u = new URL(url);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return url.length > 40 ? url.slice(0, 37) + '...' : url;
    }
  }

  toggleGroup(key: string): void {
    this.openGroupKey = this.openGroupKey === key ? null : key;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    
    // Check if click is on an icon-trigger or inside a popover
    const isIconTrigger = target.closest('.icon-trigger') !== null;
    const isInsidePopover = target.closest('.icon-popover') !== null;
    
    // If click is on icon-trigger, toggleGroup will handle it
    // If click is inside popover, keep it open
    // Otherwise, close any open tooltip
    if (!isIconTrigger && !isInsidePopover && this.openGroupKey !== null) {
      this.openGroupKey = null;
    }
  }

  closeTooltip(): void {
    this.openGroupKey = null;
  }

  updateCopyrightText(): void {
    const company = this.companyName || this.translate.instant('auth.Brand');
    const copyrightTemplate = this.translate.instant('auth.Copyright');
    // Check if translation was found (not just the key)
    if (copyrightTemplate && copyrightTemplate !== 'auth.Copyright') {
      this.copyrightText = copyrightTemplate
        .replace('{{year}}', this.currentYear.toString())
        .replace('{{companyName}}', company);
    } else {
      // Fallback if translation not loaded yet
      this.copyrightText = `© ${this.currentYear} ${company}. All rights reserved.`;
    }
  }
}